import React from "react";

import { ElementModel } from "@courses-pm2/api/src/ts/models/ElementModel";
import { Engine } from "@courses-pm2/api/src/ts/utils";

/**
 * Specifies the desired length of the preview text, given in number of
 * characters.
 */
const DESIRED_PREVIEW_TEXT_LENGTH = 150;

/**
 * Specification of the data needed to create a new instance of the
 * ContentModel.
 */
export type ContentModelData = {
    elements: ElementModel[];
    isAuthorized: boolean;
    isScrambled: boolean;
};

/**
 * The content model is used to describe data returned by the remote endpoint,
 * making it possible to combine multiple elements from a single response.
 */
export class ContentModel<T extends {} = {}> {
    /**
     * Will contain the list of elements that should be rendered by this model.
     */
    public readonly elements: ElementModel[];

    /**
     * Specifies if the user has been authorized to access this content.
     */
    public readonly isAuthorized: boolean;

    /**
     * Specifies if a single content element inside this model has been
     * scrambled.
     */
    public readonly isScrambled: boolean;

    /**
     * Will contain a reference to the engine that's used to power rendering of
     * models within the current application.
     */
    protected readonly engine?: Engine<T>;

    /**
     * Apply data when initially creating an instance of the model.
     */
    constructor(initial: ContentModelData & {
        engine?: Engine<T>;
    }) {
        this.elements = initial.elements;
        this.isAuthorized = initial.isAuthorized;
        this.isScrambled = initial.isScrambled;
        this.engine = initial.engine;
    }

    /**
     * Helper that'll render all the elements contained within this model
     * directly to React nodes, which can be mounted in the DOM.
     */
    public render(): React.ReactNode[] {
        const engine = this.engine;

        // Bail out if this model was fetched without providing a rendering
        // engine!
        if (!engine) {
            throw new Error("ContentModel.render(): No rendering engine provided to this model.");
        }

        return this.elements.map((element, index) => (
            <React.Fragment key={index}>{engine.render(element)}</React.Fragment>
        ));
    }

    /**
     * Helper that'll iterate over the list of child elements, and return the
     * url to the first available preview image (if any).
     */
    public async getPreviewImageUrl(): Promise<string | undefined> {
        for (const element of this.elements) {
            const previewImageUrl = await element.getPreviewImageUrl();

            if (previewImageUrl !== undefined) {
                return previewImageUrl;
            }
        }

        // If we get here, no preview image could be found
        return undefined;
    }

    /**
     * Helper that'll iterate over the list of child elements, and return the
     * url to the first available preview text (if any).
     */
    public getPreviewText(): string | undefined {
        const previewTexts: string[] = [];

        for (const element of this.elements) {
            const previewText = (element.getPreviewText() || "").trim();

            if (previewText) {
                previewTexts.push(previewText);
            }
        }

        // If any preview texts were found, then combind and push them
        if (previewTexts.length > 0) {
            const previewText = previewTexts.join(", ");
            const cropAt = previewText.indexOf(" ", DESIRED_PREVIEW_TEXT_LENGTH);

            return (
                cropAt !== -1
                    ? `${previewText.substr(0, cropAt)}...`
                    : previewText
            );
        }

        // If we get here, no preview image could be found
        return undefined;
    }

    /**
     * Reduce the list of contained elements to their accumulated count of
     * standard pages.
     */
    public getStandardPageCount(): number {
        return this.elements.reduce(
            (previousValue, element) => previousValue + element.getStandardPageCount(),
            0,
        );
    }
}
